import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HomeIcon from "@mui/icons-material/Home";
import { Box, Button, Typography, useColorScheme } from "@mui/joy";
import { useNavigate } from "react-router-dom";

export default function SoonPage() {
  const { mode } = useColorScheme();
  const isDark = mode === "dark";
  const navigate = useNavigate();

  const accentColor = "rgba(168, 85, 247, 0.8)"; // Purple for mystery/anticipation

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: isDark
          ? "linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #2e1a2e 100%)"
          : "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 50%, #e9d5ff 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        padding: "20px",
      }}
    >
      {/* Background magical effects */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 30% 70%, rgba(168, 85, 247, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 70% 30%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(196, 181, 253, 0.08) 0%, transparent 50%)
          `,
          zIndex: 0,
        }}
      />

      {/* Floating orbs */}
      {[...Array(8)].map((_, i) => (
        <Box
          key={`orb-${i}`}
          sx={{
            position: "absolute",
            width: `${6 + (i % 4) * 3}px`,
            height: `${6 + (i % 4) * 3}px`,
            borderRadius: "50%",
            background: `radial-gradient(circle, rgba(168, 85, 247, 0.6) 0%, transparent 70%)`,
            left: `${10 + i * 10}%`,
            top: `${15 + (i % 5) * 15}%`,
            animation: `float-${i % 3} ${8 + (i % 4)}s ease-in-out infinite`,
            animationDelay: `${i * 0.5}s`,
            "@keyframes float-0": {
              "0%, 100%": { transform: "translateY(0px) translateX(0px)" },
              "33%": { transform: "translateY(-25px) translateX(15px)" },
              "66%": { transform: "translateY(15px) translateX(-10px)" },
            },
            "@keyframes float-1": {
              "0%, 100%": { transform: "translateY(0px) translateX(0px)" },
              "50%": { transform: "translateY(-20px) translateX(-15px)" },
            },
            "@keyframes float-2": {
              "0%, 100%": { transform: "translateY(0px) translateX(0px)" },
              "25%": { transform: "translateY(20px) translateX(10px)" },
              "75%": { transform: "translateY(-15px) translateX(-15px)" },
            },
          }}
        />
      ))}

      {/* Main content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          textAlign: "center",
          maxWidth: "600px",
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              background: isDark
                ? "rgba(168, 85, 247, 0.1)"
                : "rgba(168, 85, 247, 0.15)",
              border: `2px solid ${accentColor}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "pulse 3s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": {
                  boxShadow: `0 0 20px rgba(168, 85, 247, 0.3)`,
                  transform: "scale(1)",
                },
                "50%": {
                  boxShadow: `0 0 40px rgba(168, 85, 247, 0.5)`,
                  transform: "scale(1.05)",
                },
              },
            }}
          >
            <AutoAwesomeIcon
              sx={{
                fontSize: "3.5rem",
                color: accentColor,
              }}
            />
          </Box>
        </Box>

        {/* Title */}
        <Typography
          level="h1"
          sx={{
            color: isDark ? "white" : "#1e293b",
            fontFamily: "'Cinzel', serif",
            fontWeight: 400,
            letterSpacing: "0.1em",
            mb: 2,
            textShadow: isDark ? `0 0 30px rgba(168, 85, 247, 0.3)` : "none",
          }}
        >
          Coming Soon
        </Typography>

        {/* Subtitle */}
        <Typography
          level="body-lg"
          sx={{
            color: isDark
              ? "rgba(255, 255, 255, 0.7)"
              : "rgba(30, 41, 59, 0.7)",
            fontFamily: "'EB Garamond', serif",
            fontStyle: "italic",
            mb: 4,
            lineHeight: 1.8,
          }}
        >
          You've uncovered the true extent of our laziness.
          <br />
          Maybe one day we'll finish this.
        </Typography>

        {/* Decorative line */}
        <Box
          sx={{
            width: "100px",
            height: "2px",
            background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
            margin: "0 auto 2rem",
          }}
        />

        {/* Home button */}
        <Button
          variant="outlined"
          startDecorator={<HomeIcon />}
          onClick={() => navigate("/")}
          sx={{
            background: "transparent",
            border: `1px solid ${accentColor}`,
            color: isDark ? "white" : "#1e293b",
            fontFamily: "'Cinzel', serif",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            px: 4,
            py: 1.5,
            transition: "all 0.3s ease",
            "&:hover": {
              background: `rgba(168, 85, 247, 0.1)`,
              border: `1px solid ${accentColor}`,
              transform: "translateY(-2px)",
              boxShadow: `0 4px 20px rgba(168, 85, 247, 0.3)`,
            },
          }}
        >
          Return Home
        </Button>
      </Box>
    </Box>
  );
}
